import { useRecoilState, useRecoilValue } from "recoil";
import AddButton from "../../../../components/AddButton";
import {
  AlarmAddState,
  AlarmSaving,
  SecurityAddState,
  SecurityEditState,
  SecurityEditStateFromOffice,
  AlarmModalState,
  AlarmForEdit,
  alarmDeactivateModal,
  tempAlarms,
  TempAlarm,
  RequiredInput,
} from "../atoms/atoms";
import { IAlarm, ISecurityDetails } from "../types";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useVendors } from "../hooks/useVendors";
import { useDataConnection } from "../hooks/useDataConnection";
import { useAlarmType } from "../hooks/useAlarmType";
import { useEffect, useState } from "react";
import { useAlarmsByRecordNumber } from "../hooks/useAlarmsByRecordNumber";
import useAddSingleAlarm from "../hooks/useAddSingleAlarm";
import useEditSingleAlarm from "../hooks/useEditSingleAlarm";
import { useQueryClient } from "react-query";
import { toast } from "../../../../components/toastNotification";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import useDeleteAlarm from "../hooks/useDeleteAlarm";
import AlarmCard from "../../../../components/AlarmCard";
import { useMotionCensor } from "../hooks/useMotionCensor";
import { useDoorContact } from "../hooks/useDoorContact";
import { usePanicButtons } from "../hooks/usePanicButtons";
import moment from "moment";
import SecurityAlarmForm from "components/SecurityAlarmForm";

interface AlarmProps {
  alarms?: Array<IAlarm>;
  rowForEdit: ISecurityDetails | undefined;
}
const defaultAlarm = [
  {
    parentRow: true,
    tableName: "alarm",
    vendorId: null,
  },
] as Array<IAlarm>;

const AlarmForm = ({
  rowForEdit,
  alarms = defaultAlarm,
}: AlarmProps): JSX.Element => {
  const [isShowAlarmModal, setShowAlarmModal] = useRecoilState(AlarmAddState);
  const [isAddState] = useRecoilState(SecurityAddState);
  const [isEditState] = useRecoilState(SecurityEditState);
  const [isAddInEditState, setIsAddInEditState] = useState(false);
  const [isEditInAddState, setIsEditInAddState] = useState(false);
  const [showAlarmCardModal, setShowAlarmCardModal] =
    useRecoilState(AlarmModalState);
  const [isSaving, setIsSaving] = useRecoilState(AlarmSaving);
  const [, setIsRequired] = useRecoilState(RequiredInput);
  const [showDeactivateModal, setShowDeactivateModal] =
    useRecoilState(alarmDeactivateModal);
  const queryClient = useQueryClient();
  const [forDeleteDetails, setForDeleteDetails] = useState();
  const [hasDeletePermission, setHasDeletePermission] = useState(false);
  const [, setEditStateFromOffice] = useRecoilState(
    SecurityEditStateFromOffice
  );
  const [hasFirstFieldValue, setHasFirstFieldValue] = useState(true);
  const [clickCount, setClickCount] = useState(0);
  const [alarmForEditData, setAlarmForEditData] =
    useRecoilState<any>(AlarmForEdit);

  //Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission = PERMISSIONS.permission_security_add;
  const deletePermission = PERMISSIONS.permission_security_delete;

  //#region Form
  const { register, control, watch } = useFormContext();

  const formMethods = useFormContext();
  //#endregion

  //#region Options
  const { data: Vendors } = useVendors();
  const vendorsOption = Vendors?.data?.map(
    (x: {
      id: number;
      name: string;
      contact: string;
      email: string;
      city: string;
      accountNumber: string;
      state: string;
      street: string;
      vendorType: number;
      zipcode: string;
      phone: string;
      tonerRequestEmail: string;
      tonerRequestPhone: string;
    }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.name,
        email: x.email,
        accountNumber: x.accountNumber,
        address:
          (x.street ? x.street : "") +
          " " +
          (x.city ? x.city : "") +
          " " +
          (x.state ? x.state : "") +
          " " +
          (x.zipcode ? x.zipcode : ""),
        vendorType: x.vendorType,
        vendorPhone: x.phone,
        trEmail: x.tonerRequestEmail,
        tonerPhone: x.tonerRequestPhone,
        contact: x.contact,
      };
    }
  );

  // Data Connection Field Options
  const { data: dataConnection } = useDataConnection();
  const dataConnOption = dataConnection?.data?.map(
    (x: { id: number; dataConnectionName: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.dataConnectionName,
      };
    }
  );

  // Alarm Type Field Options
  const { data: alarmType } = useAlarmType();
  const alarmTypeOption = alarmType?.data?.map(
    (x: { id: number; alarmTypeName: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.alarmTypeName,
      };
    }
  );

  // Motion Censors Field Options
  const { data: motionCensor } = useMotionCensor();
  const motionCensorOptions = motionCensor?.data?.map(
    (x: { id: number; name: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.name,
      };
    }
  );

  // Door Contact Field Options
  const { data: doorContact } = useDoorContact();
  const doorContactOptions = doorContact?.data?.map(
    (x: { id: number; numberOfDoorContact: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.numberOfDoorContact,
      };
    }
  );

  // Panic Buttons Field Options
  const { data: panicButtons } = usePanicButtons();
  const panicButtonOptions = panicButtons?.data?.map(
    (x: { id: number; numberOfPanicButton: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.numberOfPanicButton,
      };
    }
  );

  //#endregion Options

  //#region Add/minus of forms
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "alarm",
  });

  const tableArray = alarms.length > 0 && watch(`${alarms[0].tableName}`);

  const controlledFields =
    tableArray &&
    fields.map((field, index) => ({
      ...field,
      ...tableArray[index],
    }));

  //SETTING DEFAULT TABLE VALUES
  useEffect(() => {
    if (alarms) {
      replace(alarms);
    }
  }, [alarms]);
  //#endregion Add/minus

  //Add Validation
  const [initialAddAlarms, setInitialAddAlarms] = useRecoilState(tempAlarms);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  //dirty checker

  const isEmpty = (value: any): boolean => {
    return value === null || value === 0 || value === "" || value === undefined;
  };

  const requiredFields = ["vendorId"];

  const optionalFields = [
    "vendor",
    "vendorPhone",
    "contractRenewalDate",
    "accountNumber",
    "alarmSiteNumber",
    "dataConnection",
    "dataConnectionId",
    "connectionDetails",
    "alarmType",
    "alarmTypeId",
    "numberOfMotionSensors",
    "numberOfDoorContacts",
    "numberOfPanicButtons",
    "specialEquipment",
    "alarmInstallDate",
    "cancellationDate",
    "ipaddress",
    "portNumber",
    "ipGateway",
    "subnetMask",
    "dns",
  ];

  const isAlarmDirty = (alarms: any[]): boolean => {
    return alarms.some((alarm) => {
      const missingRequired = requiredFields.some((field) =>
        isEmpty(alarm[field])
      );
      if (missingRequired) return false;

      const hasOptionalData = optionalFields.some(
        (field) => !isEmpty(alarm[field])
      );
      return hasOptionalData;
    });
  };

  const checkEmptyFields = (buttonState) => {
    if (buttonState === "add") {
      setClickCount(clickCount + 1);
      watch("alarm[0].vendorId")
        ? setHasFirstFieldValue(true)
        : setHasFirstFieldValue(false);
    }
    if (buttonState === "proceed") {
      const fieldLen = controlledFields.length;
      let hasValueCount = 0;

      controlledFields.forEach((obj: IAlarm, i: number) =>
        Object.entries(obj)?.forEach(([key, value]) => {
          if (key === "vendorId") {
            if (value) {
              hasValueCount = hasValueCount + 1;
              if (fieldLen === hasValueCount) {
                setShowAlarmModal(false);
              }
            } else {
              setClickCount(clickCount + 1);
              setIsRequired(true);
              toast.fire(
                "Information",
                "Please fill the Vendor Name to proceed.",
                "info"
              );
              setHasFirstFieldValue(false);
            }
          }
        })
      );
    }
  };

  //Set Edit default values
  const setEditAlarmValues = (alarm) => {
    if (!alarm) return;
    formMethods.setValue("id", alarm.id);
    formMethods.setValue("vendorId", alarm.vendorId);
    formMethods.setValue("vendorPhone", alarm.vendorPhone);
    formMethods.setValue("accountNumber", alarm.accountNumber);
    formMethods.setValue(
      "contractRenewalDate",
      alarm.contractRenewalDate?.slice(0, 10)
    );
    formMethods.setValue("alarmSiteNumber", alarm.alarmSiteNumber);
    formMethods.setValue("dataConnectionId", alarm.dataConnectionId);
    formMethods.setValue(
      "alarmInstallDate",
      alarm.alarmInstallDate?.slice(0, 10)
    );
    formMethods.setValue("connectionDetails", alarm.connectionDetails);
    formMethods.setValue("alarmTypeId", alarm.alarmTypeId);
    formMethods.setValue("numberOfMotionSensors", alarm.numberOfMotionSensors);
    formMethods.setValue("numberOfDoorContacts", alarm.numberOfDoorContacts);
    formMethods.setValue("numberOfPanicButtons", alarm.numberOfPanicButtons);
    formMethods.setValue("specialEquipment", alarm.specialEquipment);
    formMethods.setValue(
      "cancellationDate",
      alarm.cancellationDate?.slice(0, 10)
    );
    formMethods.setValue("isActive", alarm.isActive);
    formMethods.setValue("createdBy", alarm.createdBy);
    formMethods.setValue("createdDate", alarm.createdDate);
    formMethods.setValue("ipaddress", alarm.ipaddress);
    formMethods.setValue("ipGateway", alarm.ipGateway);
    formMethods.setValue("subnetMask", alarm.subnetMask);
    formMethods.setValue("dns", alarm.dns);

    formMethods.setValue("portNumber", alarm.portNumber);
    //use to delay for isActive checkbox
    queryClient.invalidateQueries("GetAlarmsByRecordNumber");
    setShowAlarmCardModal(true);
  };
  //Single Add Initialization
  const {
    mutate: addSingleAlarm,
    isSuccess: addAlarmSuccess,
    data: addAlarmResult,
    reset: resetAddAlarmData,
  } = useAddSingleAlarm();

  //Single Edit Initialization
  const {
    mutate: editSingleAlarm,
    isSuccess: editAlarmSuccess,
    data: editAlarmResult,
    reset: resetEditAlarmData,
  } = useEditSingleAlarm();

  //Delete
  const {
    mutate: deleteAlarm,
    isSuccess: deleteAlarmSuccess,
    data: deleteAlarmResult,
    reset: resetDeleteAlarmData,
  } = useDeleteAlarm();

  //Reset form data sing Add
  const resetData = () => {
    formMethods.setValue("alarm[0].vendorId", "");
    formMethods.setValue("alarm[0].vendorPhone", "");
    formMethods.setValue("alarm[0].accountNumber", "");
    formMethods.setValue("alarm[0].contractRenewalDate", "");
    formMethods.setValue("alarm[0].alarmSiteNumber", "");
    formMethods.setValue("alarm[0].dataConnectionId", "");
    formMethods.setValue("alarm[0].alarmInstallDate", "");
    formMethods.setValue("alarm[0].connectionDetails", "");
    formMethods.setValue("alarm[0].alarmTypeId", "");
    formMethods.setValue("alarm[0].numberOfMotionSensors", "");
    formMethods.setValue("alarm[0].numberOfDoorContacts", "");
    formMethods.setValue("alarm[0].numberOfPanicButtons", "");
    formMethods.setValue("alarm[0].specialEquipment", "");
    formMethods.setValue("alarm[0].cancellationDate", "");
    formMethods.setValue("alarm[0].ipaddress", "");
    formMethods.setValue("alarm[0].ipGateway", "");
    formMethods.setValue("alarm[0].subnetMask", "");
    formMethods.setValue("alarm[0].dns", "");

    formMethods.setValue("alarm[0].portNumber", "");
  };

  //Save Get values for single add
  const handleAddSave = () => {
    const submittedValues = formMethods.getValues();

    const hasAlarm = submittedValues.alarm && submittedValues.alarm.length > 0;
    if (!hasAlarm) {
      return;
    }
    const isDirty = isAlarmDirty(submittedValues.alarm);
    if (!isDirty) {
      setIsRequired(true);
      toast.fire(
        "Information",
        "Please fill the vendor field to save.",
        "info"
      );
      return;
    }
    setIsSaving(true);
    const alarmData = {
      recordNumber: rowForEdit?.recordNumber,
      vendorId: formMethods.getValues("alarm[0].vendorId"),
      vendorPhone: formMethods.getValues("alarm[0].vendorPhone"),
      accountNumber: formMethods.getValues("alarm[0].accountNumber"),
      contractRenewalDate: formMethods.getValues(
        "alarm[0].contractRenewalDate"
      ),
      alarmSiteNumber: formMethods.getValues("alarm[0].alarmSiteNumber").trim(),
      dataConnectionId: formMethods.getValues("alarm[0].dataConnectionId"),
      alarmInstallDate: formMethods.getValues("alarm[0].alarmInstallDate"),
      connectionDetails: formMethods
        .getValues("alarm[0].connectionDetails")
        .trim(),
      alarmTypeId: formMethods.getValues("alarm[0].alarmTypeId"),
      numberOfMotionSensors: formMethods.getValues(
        "alarm[0].numberOfMotionSensors"
      ),
      numberOfDoorContacts: formMethods.getValues(
        "alarm[0].numberOfDoorContacts"
      ),
      numberOfPanicButtons: formMethods.getValues(
        "alarm[0].numberOfPanicButtons"
      ),
      specialEquipment: formMethods
        .getValues("alarm[0].specialEquipment")
        .trim(),
      cancellationDate: formMethods.getValues("alarm[0].cancellationDate"),
      ipaddress: formMethods.getValues("alarm[0].ipaddress").trim(),
      ipGateway: formMethods.getValues("alarm[0].ipGateway").trim(),
      subnetMask: formMethods.getValues("alarm[0].subnetMask").trim(),
      dns: formMethods.getValues("alarm[0].dns").trim(),

      portNumber: formMethods.getValues("alarm[0].portNumber").trim(),
    };
    addSingleAlarm(alarmData);
    setShowAlarmModal(false);
  };

  //Save Edit single alarm
  const handleEditSave = () => {
    setIsSaving(true);
    const alarmNewData = {
      recordNumber: rowForEdit?.recordNumber,
      id: formMethods.getValues("id"),
      vendorId: formMethods.getValues("vendorId"),
      vendorPhone: formMethods.getValues("vendorPhone"),
      accountNumber: formMethods.getValues("accountNumber"),
      contractRenewalDate: formMethods.getValues("contractRenewalDate")
        ? formMethods.getValues("contractRenewalDate").slice(0, 10)
        : null,
      alarmSiteNumber: formMethods.getValues("alarmSiteNumber")?.trim() ?? null,
      dataConnectionId: formMethods.getValues("dataConnectionId"),
      alarmInstallDate: formMethods.getValues("alarmInstallDate")
        ? formMethods.getValues("alarmInstallDate").slice(0, 10)
        : null,
      connectionDetails:
        formMethods.getValues("connectionDetails")?.trim() ?? null,
      alarmTypeId: formMethods.getValues("alarmTypeId"),
      numberOfMotionSensors: formMethods.getValues("numberOfMotionSensors"),
      numberOfDoorContacts: formMethods.getValues("numberOfDoorContacts"),
      numberOfPanicButtons: formMethods.getValues("numberOfPanicButtons"),
      specialEquipment:
        formMethods.getValues("specialEquipment")?.trim() ?? null,
      cancellationDate: formMethods.getValues("cancellationDate")
        ? formMethods.getValues("cancellationDate").slice(0, 10)
        : null,
      isActive: formMethods.getValues("isActive"),
      createdBy: formMethods.getValues("createdBy"),
      createdDate: formMethods.getValues("createdDate"),
      ipaddress: formMethods.getValues("ipaddress")?.trim() ?? null,
      portNumber: formMethods.getValues("portNumber")?.trim() ?? null,
      ipGateway: formMethods.getValues("ipGateway")?.trim() ?? null,
      subnetMask: formMethods.getValues("subnetMask")?.trim() ?? null,
      dns: formMethods.getValues("dns")?.trim() ?? null,
    };
    if (!alarmNewData.vendorId) {
      toast.fire("Information", "Please fill the Vendor Name to save.", "info");
      setIsRequired(true);
      setIsSaving(false);
      return;
    }
    const alarmOldData = {
      recordNumber: alarmForEditData?.recordNumber,
      id: alarmForEditData?.id,
      vendorId: alarmForEditData?.vendorId,
      vendorPhone: alarmForEditData?.vendorPhone,
      accountNumber: alarmForEditData?.accountNumber,
      contractRenewalDate: alarmForEditData?.contractRenewalDate
        ? alarmForEditData?.contractRenewalDate?.slice(0, 10)
        : null,
      alarmSiteNumber: alarmForEditData?.alarmSiteNumber,
      dataConnectionId: alarmForEditData?.dataConnectionId,
      alarmInstallDate: alarmForEditData?.alarmInstallDate
        ? alarmForEditData?.alarmInstallDate?.slice(0, 10)
        : null,
      connectionDetails: alarmForEditData?.connectionDetails,
      alarmTypeId: alarmForEditData?.alarmTypeId,
      numberOfMotionSensors: alarmForEditData?.numberOfMotionSensors,
      numberOfDoorContacts: alarmForEditData?.numberOfDoorContacts,
      numberOfPanicButtons: alarmForEditData?.numberOfPanicButtons,
      specialEquipment: alarmForEditData?.specialEquipment,
      cancellationDate: alarmForEditData?.cancellationDate
        ? alarmForEditData?.cancellationDate?.slice(0, 10)
        : null,
      isActive: alarmForEditData?.isActive,
      createdBy: alarmForEditData?.createdBy,
      createdDate: alarmForEditData?.createdDate,
      ipaddress: alarmForEditData?.ipaddress,
      portNumber: alarmForEditData?.portNumber,
      ipGateway: alarmForEditData?.ipGateway,
      subnetMask: alarmForEditData?.subnetMask,
      dns: alarmForEditData?.dns,
    };

    const newData = JSON.stringify(alarmNewData);
    const oldData = JSON.stringify(alarmOldData);

    if (newData === oldData) {
      toast.fire("Saving Failed", "No changes have been made.", "warning");
      setIsSaving(false);
    } else {
      setIsSaving(true);
      editSingleAlarm(alarmNewData);
      setShowAlarmCardModal(false);
    }
  };

  //Deactivate
  const handleDeactivate = () => {
    deleteAlarm(forDeleteDetails);
    setShowDeactivateModal(false);
  };

  //Modal Messages
  const deleteMessage = (
    <div className="text-gray-500 font-normal">
      {"Are you sure you want to deactivate this Alarm?"}
    </div>
  );

  const removeMessage = (
    <div className="text-gray-500 font-normal">
      {"Are you sure you want to remove this Alarm?"}
    </div>
  );

  //Toasters
  if (addAlarmSuccess && addAlarmResult) {
    if (addAlarmResult.status === 201 || addAlarmResult.status === 200) {
      toast.fire("Success", "Alarm added successfully.", "success");
      queryClient.invalidateQueries("GetAlarmsByRecordNumber");
    }
    setIsSaving(false);
    resetAddAlarmData();
    resetData();
  }
  if (editAlarmSuccess && editAlarmResult) {
    if (editAlarmResult.status === 201 || editAlarmResult.status === 200) {
      toast.fire("Success", "Alarm updated successfully.", "success");
      queryClient.invalidateQueries("GetAlarmsByRecordNumber");
    }
    setIsSaving(false);
    resetEditAlarmData();
  }
  if (deleteAlarmSuccess && deleteAlarmResult) {
    if (deleteAlarmResult.status === 201 || deleteAlarmResult.status === 200) {
      toast.fire("Success", "Alarm deactivated successfully.", "success");
      queryClient.invalidateQueries("GetAlarmsByRecordNumber");
    }
    resetDeleteAlarmData();
  }

  const { data: alarmsData } = useAlarmsByRecordNumber(
    rowForEdit ? rowForEdit?.recordNumber : 0
  );

  useEffect(() => {
    if (userPermissions.includes(deletePermission)) {
      setHasDeletePermission(true);
    }
  }, []);

  //reset form when adding
  const resetAlarmForm = () => {
    remove();
    append({
      vendorId: null,
      vendor: "",
      vendorPhone: "",
      contractRenewalDate: null,
      accountNumber: "",
      alarmSiteNumber: "",
      dataConnection: "",
      dataConnectionId: null,
      connectionDetails: "",
      alarmTypeId: 0,
      alarmType: "",
      numberOfMotionSensors: "",
      numberOfDoorContacts: "",
      numberOfPanicButtons: "",
      specialEquipment: "",
      alarmInstallDate: "",
      cancellationDate: "",
      ipaddress: "",
      portNumber: "",
      ipGateway: "",
      subnetMask: "",
      dns: "",
    });

    setAlarmForEditData(null);
    setEditingIndex(null);
  };

  //INITIAL ADD
  //removing of array in initial add
  const [indexToRemove, setIndexToRemove] = useState<number | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const handleRemove = () => {
    if (indexToRemove !== null) {
      setInitialAddAlarms((prevData) =>
        prevData.filter((_, i) => i !== indexToRemove)
      );
      setIndexToRemove(null);
    }
    setShowRemoveModal(false);
  };
  const handleOpenCardModalRemove = (index) => {
    setIndexToRemove(index);
    setShowRemoveModal(true);
  };

  const handleInitialAdd = (buttonState) => {
    if (buttonState === "proceed") {
      const submittedValues = formMethods.getValues();

      const isDirty = isAlarmDirty(submittedValues.alarm);
      if (!isDirty) return;

      const alarmDtos: TempAlarm[] = submittedValues.alarm.map((item: any) => {
        const alarmTypeName =
          alarmTypeOption?.find((a) => a.id === item.alarmTypeId)?.label || "";
        const vendorName =
          vendorsOption?.find((v) => v.id === item.vendorId)?.label || "";
        const vendorPhone =
          vendorsOption?.find((v) => v.id === item.vendorId)?.vendorPhone || "";
        const dataConnectionName =
          dataConnOption?.find((d) => d.id === item.dataConnectionId)?.label ||
          "";

        return {
          vendorId: item.vendorId ?? null,
          vendorName: vendorName,
          vendorPhone: vendorPhone,
          contractRenewalDate: item.contractRenewalDate || null,
          accountNumber: item.accountNumber || "",
          alarmSiteNumber: item.alarmSiteNumber || "",
          dataConnection: dataConnectionName,
          dataConnectionId: item.dataConnectionId ?? null,
          connectionDetails: item.connectionDetails || "",
          alarmTypeId: item.alarmTypeId || null,
          alarmTypeName: alarmTypeName,
          numberOfMotionSensors: item.numberOfMotionSensors || "",
          numberOfDoorContacts: item.numberOfDoorContacts || "",
          numberOfPanicButtons: item.numberOfPanicButtons || "",
          specialEquipment: item.specialEquipment || "",
          alarmInstallDate: item.alarmInstallDate || "",
          cancellationDate: item.cancellationDate || "",
          isActive: item.isActive ?? true,
          ipaddress: item.ipaddress || "",
          portNumber: item.portNumber || "",
          ipGateway: item.ipGateway || "",
          subnetMask: item.subnetMask || "",
          dns: item.dns || "",
        };
      });
      setInitialAddAlarms((prev) => [...(prev || []), ...alarmDtos]);
    }
  };

  const handleInitialEdit = () => {
    const formData = formMethods.getValues();

    if (!formData.vendorId) {
      toast.fire(
        "Information",
        "Please fill the Vendor Name to proceed.",
        "info"
      );
      setIsRequired(true);

      return;
    }
    const alarmTypeName =
      alarmTypeOption?.find((a) => a.id === formData.alarmTypeId)?.label || "";
    const vendorName =
      vendorsOption?.find((v) => v.id === formData.vendorId)?.label || "";
    const vendorPhone =
      vendorsOption?.find((v) => v.id === formData.vendorId)?.vendorPhone || "";
    const dataConnection =
      dataConnOption?.find((d) => d.id === formData.dataConnectionId)?.label ||
      "";
    const payload = {
      vendorId: formData.vendorId ?? null,
      vendorName,
      vendorPhone,
      contractRenewalDate: formData.contractRenewalDate
        ? moment.utc(formData.contractRenewalDate).format("YYYY-MM-DD")
        : null,
      accountNumber: formData.accountNumber ?? "",
      alarmSiteNumber: formData.alarmSiteNumber ?? "",
      dataConnection,
      dataConnectionId: formData.dataConnectionId ?? null,
      connectionDetails: formData.connectionDetails ?? "",
      alarmTypeId: formData.alarmTypeId ?? 0,
      alarmTypeName,
      numberOfMotionSensors: formData.numberOfMotionSensors ?? "",
      numberOfDoorContacts: formData.numberOfDoorContacts ?? "",
      numberOfPanicButtons: formData.numberOfPanicButtons ?? "",
      specialEquipment: formData.specialEquipment ?? "",
      alarmInstallDate: formData.alarmInstallDate
        ? moment.utc(formData.alarmInstallDate).format("YYYY-MM-DD")
        : "",
      cancellationDate: formData.cancellationDate ?? "",
      isActive: formData.isActive ?? true,
      ipaddress: formData.ipaddress ?? "",
      portNumber: formData.portNumber ?? "",
      ipGateway: formData.ipGateway ?? "",
      subnetMask: formData.subnetMask ?? "",
      dns: formData.dns ?? "",
    };

    if (editingIndex !== null) {
      const updatedList = [...initialAddAlarms];
      updatedList[editingIndex] = payload;

      setInitialAddAlarms(updatedList);
      setEditingIndex(null);
      setIsEditInAddState(false);
      setShowAlarmCardModal(false);
    } else {
      console.log("No editing index");
    }
  };

  return (
    <div className="mx-2 mt-2 bg-white box-shadow rounded-lg h-full">
      <div className="flex flex-row justify-between py-2">
        <div className="text-quinary font-bold font-sans px-4">
          Alarm Section
        </div>
        {userPermissions.includes(addPermission) ? (
          <div className="py-2 px-4">
            <AddButton
              className={"opacity-100"}
              disabled={
                alarmsData !== undefined
                  ? alarmsData.length >= 5
                    ? true
                    : false
                  : false
              }
              buttonText="Add Alarm"
              onClick={() => {
                setShowAlarmModal(true);
                setIsAddInEditState(true);
                resetAlarmForm();
                setIsRequired(false);
              }}
            />
          </div>
        ) : (
          <div className="py-5 px-4"></div>
        )}
      </div>

      {/* INITIAL ADD */}
      {isAddState && (
        <div className="text-quinary font-bold font-sans py-2 px-4  grid grid-cols-5 gap-x-4">
          {Array.isArray(initialAddAlarms) && initialAddAlarms.length > 0 ? (
            initialAddAlarms.map((alarm, index) => (
              <AlarmCard
                key={index}
                alarm={alarm}
                isEditState={true}
                hasDeletePermission={hasDeletePermission}
                onClickView={() => {}}
                onClickEdit={() => {
                  setEditAlarmValues(alarm);
                  setAlarmForEditData(alarm); //recoil
                  setEditingIndex(index);
                  setIsEditInAddState(true);
                  setIsRequired(false);
                }}
                onClickDelete={() => {
                  setShowDeactivateModal(true);
                }}
                hideDelete
                onClickRemove={() => {
                  handleOpenCardModalRemove(index);
                }}
                hideRemove={
                  (indexToRemove !== null && index === indexToRemove) ||
                  showAlarmCardModal
                }
                editHidden={
                  (indexToRemove !== null && index === indexToRemove) ||
                  showAlarmCardModal
                }
              />
            ))
          ) : (
            <div></div>
          )}
        </div>
      )}

      {/* Show card edit button if edit state*/}
      {isEditState && (
        <div className="w-full borderContainer -mt-4 pb-4 bg-white px-4">
          <div className="grid gap-2 grid-cols-5">
            {/* Card */}
            {alarmsData?.map((alarm) => (
              <div key={alarm.id}>
                <AlarmCard
                  alarm={alarm}
                  isEditState={isEditState}
                  onClickView={() => {}}
                  onClickEdit={() => {
                    setEditAlarmValues(alarm);
                    setAlarmForEditData(alarm);
                    setIsRequired(false);
                  }}
                  onClickDelete={() => {
                    setShowDeactivateModal(true);
                    setForDeleteDetails(alarm);
                  }}
                  hasDeletePermission={hasDeletePermission}
                  hideDelete={showAlarmCardModal}
                  editHidden={showAlarmCardModal}
                  hideRemove
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add */}
      {isShowAlarmModal && (
        <SecurityAlarmForm
          isOpen={isShowAlarmModal}
          onClose={() => setShowAlarmModal(false)}
          mode={isAddState ? "initialAdd" : "add"}
          isSaving={isSaving}
          onSubmit={() => {
            if (isAddState) {
              checkEmptyFields("proceed");
              handleInitialAdd("proceed");
              setHasFirstFieldValue(true);
            } else {
              checkEmptyFields("save");
              handleAddSave();
            }
          }}
          formMethods={formMethods}
          vendorsOption={vendorsOption}
          dataConnOption={dataConnOption}
          alarmTypeOption={alarmTypeOption}
          motionCensorOptions={motionCensorOptions}
          doorContactOptions={doorContactOptions}
          panicButtonOptions={panicButtonOptions}
        />
      )}

      {/* Edit */}
      {showAlarmCardModal && (
        <SecurityAlarmForm
          isOpen={showAlarmCardModal}
          onClose={() => setShowAlarmCardModal(false)}
          mode={isEditInAddState ? "initialEdit" : "edit"}
          isSaving={isSaving}
          onSubmit={() => {
            if (isEditInAddState) {
              handleInitialEdit();
            } else {
              handleEditSave();
            }
          }}
          formMethods={formMethods}
          vendorsOption={vendorsOption}
          dataConnOption={dataConnOption}
          alarmTypeOption={alarmTypeOption}
          motionCensorOptions={motionCensorOptions}
          doorContactOptions={doorContactOptions}
          panicButtonOptions={panicButtonOptions}
          hasDeletePermission={hasDeletePermission}
          isEditState={isEditState}
        />
      )}

      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <ConfirmBoxDelete
          ModalTitle="Security Alarm"
          qConfirmation={deleteMessage}
          isOpen={showDeactivateModal}
          onClose={() => setShowDeactivateModal(false)}
          onClickFunction={() => handleDeactivate()}
          cancelButtonRef={undefined}
        />
      )}

      {showRemoveModal && (
        <ConfirmBoxDelete
          ModalTitle="Alarm"
          qConfirmation={removeMessage}
          isOpen={showRemoveModal}
          onClose={() => {
            setShowRemoveModal(false);
            setIndexToRemove(null);
          }}
          onClickFunction={handleRemove}
          cancelButtonRef={undefined}
        />
      )}
    </div>
  );
};

export default AlarmForm;
